import React, { useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useNodePerformanceContext, useReactNodeView } from '@lyearn/lygo-core';
import { AudioPlayer } from '@lyearn/shared/components/Players';
import { Box, useSnackbar } from '@lyearn/ui';

import Caption from '../../../components/ResizeContainer/Caption';
import { AudioComponentProps } from '../types';

const AudioComponentReadOnly: React.FC<AudioComponentProps> = (props) => {
  const { src, caption, allowForwarding } = props;
  const { node, view } = useReactNodeView();
  const { showErrorSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { setNodePerformance, getNodePerformance } = useNodePerformanceContext();

  const setPerformance = useCallback(
    (progress: number) => {
      node?.attrs.id && setNodePerformance?.({ id: node?.attrs.id, blockType: 'audio', progress });
    },
    [node?.attrs.id, setNodePerformance],
  );

  const showSnackbarError = useCallback(() => {
    showErrorSnackbar({
      message: t('Lygo:AdvancedMenu.audio.AllowForwarding.error'),
      position: { horizontal: 'right', vertical: 'bottom' },
    });
  }, [showErrorSnackbar, t]);

  const onProgress = useCallback(
    (attrs: { time: number }) => {
      setPerformance(Math.ceil((attrs.time / node?.attrs.duration) * 100));
    },
    [node?.attrs.duration, setPerformance],
  );

  const { maxBlockProgress, initalBlockProgress } = useMemo(() => {
    return {
      maxBlockProgress: getNodePerformance?.(node?.attrs.id)?.progress || 0,
      initalBlockProgress: getNodePerformance?.(node?.attrs.id)?.progress,
    };
  }, [getNodePerformance, node?.attrs.id]);

  useEffect(() => {
    // set performance for empty block where video is still to be attach
    if (!node?.attrs.src && !view?.editable) {
      setPerformance?.(100);
    }
  }, [node?.attrs.src, setPerformance, view?.editable]);

  if (!src) {
    return null;
  }

  return (
    <Box className="mt-32 mb-12">
      <AudioPlayer
        initialProgress={initalBlockProgress}
        maxPlayerProgress={maxBlockProgress}
        url={src}
        onProgress={onProgress}
        onSeek={!allowForwarding ? showSnackbarError : undefined}
      />
      <Caption readOnly caption={caption} />
    </Box>
  );
};

export default AudioComponentReadOnly;
